import { ActionType } from '@src/monitoring/types/enums';
import { useAnalytics, useVideoCreatorStore } from '@src/stores-v2';
import { useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

/**
 * Public API returned by `useStoryUploadAnalytics`.
 *
 * Lifecycle:
 *  - trackInit(): starts the STORY_UPLOAD user action (idempotent)
 *  - trackSuccess(): marks the action successful + captures duration
 *  - trackFailure(): marks the action failed with error
 *  - trackCancel(): cancels the action (user closed / navigated away)
 *
 * Previously this hook only emitted a single `story_upload` event via trackEvent
 * so we lacked success/failure attribution and duration metrics. We now leverage
 * the monitoring lifecycle APIs (startUserAction / completeUserAction / failUserAction / cancelUserAction)
 * for parity with `useMonitorStoryUpload` while keeping a lightweight interface
 * for legacy Dashboard usage.
 */
interface StoryUploadMetadata {
  fileSizeBytes?: number;
  fileType?: string;
  isConcat?: boolean;
  fileCount?: number;
  isUrlUpload?: boolean;
  // Organization / showcase context
  showcase?: {
    id?: string;
    name?: string;
    slug?: string;
  };
  // Generic status fields
  status?: 'success' | 'failure' | 'cancelled';
  durationMs?: number;
  reason?: string;
  // Error classification (from UploadErrorClassifier)
  errorKind?: string;
  retryable?: boolean;
}

type ReturnType = {
  /** Correlated transaction id for the STORY_UPLOAD action */
  transactionId: string | null;
  /** Start / initialize the analytics action. Safe to call multiple times. */
  trackInit: (params?: StoryUploadMetadata) => void;
  /** Mark success (ignored if never initialized). */
  trackSuccess: (extra?: StoryUploadMetadata) => void;
  /** Mark failure (ignored if never initialized). */
  trackFailure: (error: unknown, extra?: StoryUploadMetadata) => void;
  /** Mark cancellation (ignored if never initialized). */
  trackCancel: (reason?: string) => void;
  /** Arbitrary log message correlated to the transaction (debug/info/warn/error). */
  analyticsLog: (
    level: LogLevel,
    message: string,
    payload?: StoryUploadMetadata,
  ) => void;
  /** Current analytics context snapshot (showcase + user/env). */
  analyticsContext: {
    showcase?: StoryUploadMetadata['showcase'];
    user?: unknown;
    env?: unknown;
  };
};
// (Legacy placeholder removed) Params type no longer needed.

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export function useStoryUploadAnalytics(): ReturnType {
  const transactionId = useRef<string | null>(null);
  const [, forceRerender] = useState(0); // minimal state to reflect transaction id externally
  const showcaseProperties = useRef<StoryUploadMetadata['showcase']>({});
  const startedAtRef = useRef<number | null>(null);
  const finalizedRef = useRef(false); // ensure single terminal event

  const analyticsStore = useAnalytics();
  const videoCreator = useVideoCreatorStore();

  /** Internal helper building common metadata bag. */
  const buildBaseMetadata = (extra?: StoryUploadMetadata) => ({
    showcase: showcaseProperties.current,
    ...(extra || {}),
  });

  const trackInit = (event?: StoryUploadMetadata) => {
    if (transactionId.current) return; // idempotent
    transactionId.current = uuid().substring(0, 8);
    startedAtRef.current = performance.now();
    showcaseProperties.current = {
      id: videoCreator.organization?.id,
      name: videoCreator.organization?.title,
    };
    // Start lifecycle action instead of a one-off event
    const actionId = analyticsStore.startUserAction(
      'story_upload',
      ActionType.ACTION,
      undefined,
      {
        business: {
          organization: videoCreator.organization?.id
            ? {
                id: videoCreator.organization.id,
                name: videoCreator.organization.title || 'unknown_org',
                slug: videoCreator.organization.slug,
              }
            : undefined,
          environment: analyticsStore.getTrackedEnvData()?.env || 'unknown_env',
        },
      },
      buildBaseMetadata(event),
    );
    // Use underlying monitoring action id for correlation and keep legacy short id for logs
    transactionId.current = actionId || transactionId.current;
    forceRerender(n => n + 1); // expose updated id
  };

  const trackSuccess = (extra?: StoryUploadMetadata) => {
    if (!transactionId.current || finalizedRef.current) return;
    const durationMs = startedAtRef.current
      ? performance.now() - startedAtRef.current
      : undefined;
    analyticsStore.completeUserAction(
      transactionId.current,
      buildBaseMetadata({
        status: 'success',
        durationMs,
        ...extra,
      }),
    );
    finalizedRef.current = true;
  };

  const trackFailure = (error: unknown, extra?: StoryUploadMetadata) => {
    if (!transactionId.current || finalizedRef.current) return;
    const durationMs = startedAtRef.current
      ? performance.now() - startedAtRef.current
      : undefined;
    analyticsStore.failUserAction(
      transactionId.current,
      error instanceof Error ? error : String(error),
      buildBaseMetadata({ status: 'failure', durationMs, ...extra }),
    );
    finalizedRef.current = true;
  };

  const trackCancel = (reason?: string) => {
    if (!transactionId.current || finalizedRef.current) return;
    const durationMs = startedAtRef.current
      ? performance.now() - startedAtRef.current
      : undefined;
    analyticsStore.cancelUserAction(
      transactionId.current,
      reason || 'user_cancelled',
    );
    // Emit a diagnostic log with duration snapshot (optional)
    analyticsStore.log({
      label: 'story-upload-analytics',
      level: 'info',
      transactionId: transactionId.current,
      msg: 'story_upload_cancelled',
      extraProperties: buildBaseMetadata({
        status: 'cancelled',
        durationMs,
        reason,
      }),
    });
    finalizedRef.current = true;
  };

  const analyticsLog = (
    level: LogLevel,
    message: string,
    payload?: StoryUploadMetadata,
  ) => {
    analyticsStore.log({
      label: 'story-upload-analytics',
      level,
      transactionId: transactionId.current,
      msg: message,
      extraProperties: buildBaseMetadata(payload),
    });
  };

  return {
    trackInit,
    trackSuccess,
    trackFailure,
    trackCancel,
    analyticsLog,
    transactionId: transactionId.current,
    analyticsContext: {
      showcase: showcaseProperties.current,
      ...analyticsStore.getTrackedUserData(),
      ...analyticsStore.getTrackedEnvData(),
    },
  };
}
